import {z} from 'zod';

import {TaskResponseBody} from '@dms/api/core';
import {LeadQualityEnumSchema, UtmParametersSchema} from '@dms/api/shared';

export const WorkshopInterestStateSchema = z.enum(['NEW', 'OPEN', 'CLOSED', 'UNSUCCESSFUL']);
export type WorkshopInterestState = z.infer<typeof WorkshopInterestStateSchema>;

export const WorkshopInterestActionSchema = z.enum([
  'SET_SOURCE',
  'CHANGE_BRANCH',
  'CHANGE_CUSTOMER',
  'DELETE',
  'OPEN',
  'END',
  'CLOSE',
  'REOPEN',
  'UPDATE_VEHICLE_DETAILS',
  'UPDATE_VEHICLE_INFORMATION',
  'SELECT_VEHICLE',
  'REMOVE_VEHICLE',
  'ADD_CUSTOMER_REQUEST',
  'UPDATE_CUSTOMER_REQUEST',
  'REMOVE_CUSTOMER_REQUEST',
  'UPDATE_VISIT_REQUEST',
  'SCHEDULE_VISIT',
  'UPDATE_ADDITIONAL_SERVICES',
  'CONTACT_CUSTOMER_VIA_EMAIL',
  'CONTACT_CUSTOMER_VIA_SMS',
  'CREATE_ACTIVITY',
]);
export type WorkshopInterestAction = z.infer<typeof WorkshopInterestActionSchema>;

export const ScheduledVisitResponseBodySchema = z.object({
  scheduledAt: z.string().nullable(),
  plannerUrl: z.string().nullable(),
  serviceCaseNumber: z.string().nullable(),
});
export type ScheduledVisitResponseBody = z.infer<typeof ScheduledVisitResponseBodySchema>;

export const WorkshopInterestResponseBodySchema = z.object({
  id: z.string(),
  publicId: z.string(),
  state: WorkshopInterestStateSchema,
  branchId: z.string(),
  sourceCodeId: z.string(),
  customerId: z.string(),
  contactPersonId: z.string().nullable(),
  vehicleId: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedBy: z.string(),
  updatedAt: z.string(),
  firstContactAt: z.string().nullable(),
  scheduledVisit: ScheduledVisitResponseBodySchema.nullable(),
  availableActions: z.array(WorkshopInterestActionSchema),
  leadQuality: LeadQualityEnumSchema.nullable(),
  utm: UtmParametersSchema,
});
export type WorkshopInterestResponseBody = z.infer<typeof WorkshopInterestResponseBodySchema>;

export const CreateWorkshopInterestRequestBodySchema = z.object({
  sourceCodeId: z.string(),
  customerId: z.string(),
});
export type CreateWorkshopInterestRequestBody = z.infer<
  typeof CreateWorkshopInterestRequestBodySchema
>;

export type CreateWorkshopInterestApiArg = {
  createWorkshopInterestRequestBody: CreateWorkshopInterestRequestBody;
};

export const GetWorkshopInterestApiArgSchema = z.object({
  id: z.string(),
});
export type GetWorkshopInterestApiArg = z.infer<typeof GetWorkshopInterestApiArgSchema>;

export type GetWorkshopInterestApiResponse = WorkshopInterestResponseBody;

export const ChangeWorkshopInterestBranchRequestBodySchema = z.object({
  branchId: z.string(),
});
export type ChangeWorkshopInterestBranchRequestBody = z.infer<
  typeof ChangeWorkshopInterestBranchRequestBodySchema
>;

export type ChangeWorkshopInterestBranchApiArg = {
  id: string;
  changeWorkshopInterestBranchRequestBody: ChangeWorkshopInterestBranchRequestBody;
};

export const ChangeWorkshopInterestCustomerRequestBodySchema = z.object({
  customerId: z.string(),
});
export type ChangeWorkshopInterestCustomerRequestBody = z.infer<
  typeof ChangeWorkshopInterestCustomerRequestBodySchema
>;

export type ChangeWorkshopInterestCustomerApiArg = {
  id: string;
  changeWorkshopInterestCustomerRequestBody: ChangeWorkshopInterestCustomerRequestBody;
};

export const SetWorkshopInterestSourceRequestBodySchema = z.object({
  sourceCodeId: z.string(),
});
export type SetWorkshopInterestSourceRequestBody = z.infer<
  typeof SetWorkshopInterestSourceRequestBodySchema
>;

export type SetWorkshopInterestSourceApiArg = {
  id: string;
  setWorkshopInterestSourceRequestBody: SetWorkshopInterestSourceRequestBody;
};

export const UpdateWorkshopInterestUtmRequestBodySchema = UtmParametersSchema;
export type UpdateWorkshopInterestUtmRequestBody = z.infer<
  typeof UpdateWorkshopInterestUtmRequestBodySchema
>;

export type UpdateWorkshopInterestUtmApiArg = {
  id: string;
  updateWorkshopInterestUtmRequestBody: UpdateWorkshopInterestUtmRequestBody;
};

export const WorkshopInterestActivityTypeSchema = z.enum([
  'INTEREST_CREATED',
  'INTEREST_OPENED',
  'INTEREST_REOPENED',
  'INTEREST_VISIT_SCHEDULED',
  'INTEREST_CLOSED',
  'INTEREST_OPPORTUNITY_LOST',
  'EMAIL_TO_CUSTOMER',
  'SMS_TO_CUSTOMER',
  'CALL_FROM_CUSTOMER',
  'CALL_TO_CUSTOMER',
  'MESSAGE_FROM_CUSTOMER',
  'MESSAGE_TO_CUSTOMER',
  'CUSTOMER_VISIT',
  'TASK_FINISHED',
]);
export type WorkshopInterestActivityType = z.infer<typeof WorkshopInterestActivityTypeSchema>;

export const WorkshopActivityRelatedResourceIdSchema = z.enum(['TASK', 'SMS', 'EMAIL']);
export type WorkshopActivityRelatedResourceId = z.infer<
  typeof WorkshopActivityRelatedResourceIdSchema
>;

export const WorkshopActivityRelatedTaskResponseBodySchema = z.object({
  id: z.string(),
  title: z.string(),
  priority: z.enum(['LOW', 'MEDIUM', 'HIGH']),
  type: z.enum(['TASK', 'MEETING', 'CALL']),
  state: z.enum(['TODO', 'IN_PROGRESS', 'DONE']),
  assignedTo: z.string(),
  dueDate: z.string().nullable(),
  dueFrom: z.string().nullable(),
  dueTo: z.string().nullable(),
  finishedAt: z.string().nullable(),
  description: z.string().nullable(),
});
export type WorkshopActivityRelatedTaskResponseBody = z.infer<
  typeof WorkshopActivityRelatedTaskResponseBodySchema
>;

export const WorkshopActivityRelatedSmsResponseBodySchema = z.object({
  id: z.string(),
  body: z.string(),
  sentTo: z.string().nullable().optional(),
});
export type WorkshopActivityRelatedSmsResponseBody = z.infer<
  typeof WorkshopActivityRelatedSmsResponseBodySchema
>;

export const WorkshopActivityRelatedEmailAttachmentSchema = z.object({
  filename: z.string(),
  uri: z.string(),
  contentType: z.string().nullable(),
  size: z.number().nullable(),
});

export const WorkshopActivityRelatedEmailSenderSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
});

export const WorkshopActivityRelatedEmailResponseBodySchema = z.object({
  id: z.string(),
  body: z.string(),
  subject: z.string(),
  sender: WorkshopActivityRelatedEmailSenderSchema,
  sentTo: z.string().nullable().optional(),
  attachments: z.array(WorkshopActivityRelatedEmailAttachmentSchema).optional(),
});
export type WorkshopActivityRelatedEmailResponseBody = z.infer<
  typeof WorkshopActivityRelatedEmailResponseBodySchema
>;

export const WorkshopActivityRelatedRecordSchema = z.union([
  WorkshopActivityRelatedTaskResponseBodySchema,
  WorkshopActivityRelatedSmsResponseBodySchema,
  WorkshopActivityRelatedEmailResponseBodySchema,
]);
export type WorkshopActivityRelatedRecord = z.infer<typeof WorkshopActivityRelatedRecordSchema>;

export const WorkshopInterestActivityResponseBodySchema = z.object({
  id: z.string(),
  interestId: z.string(),
  activityType: WorkshopInterestActivityTypeSchema,
  userId: z.string().nullable(),
  timestamp: z.string(),
  reasonCloseCodeId: z.string().nullable(),
  reasonCloseNote: z.string().nullable(),
  relatedRecordId: z.string().nullable(),
  relatedResourceId: WorkshopActivityRelatedResourceIdSchema.nullable(),
  description: z.string().nullable(),
  subject: z.string().nullable(),
  relatedRecord: WorkshopActivityRelatedRecordSchema.nullable(),
});
export type WorkshopInterestActivityResponseBody = z.infer<
  typeof WorkshopInterestActivityResponseBodySchema
>;

export const ListWorkshopInterestActivitiesApiArgSchema = z.object({
  interestId: z.string(),
});
export type ListWorkshopInterestActivitiesApiArg = z.infer<
  typeof ListWorkshopInterestActivitiesApiArgSchema
>;

export type ListWorkshopInterestActivitiesApiResponse = WorkshopInterestActivityResponseBody[];

export type DeleteWorkshopInterestApiArg = {
  id: string;
};

export type OpenWorkshopInterestApiArg = {
  id: string;
};

export type ReopenWorkshopInterestApiArg = {
  id: string;
};

export type CloseWorkshopInterestApiArg = {
  id: string;
};

export const EndWorkshopInterestRequestBodySchema = z.object({
  closeReasonCodeId: z.string(),
  closeNote: z.string().nullable(),
});
export type EndWorkshopInterestRequestBody = z.infer<typeof EndWorkshopInterestRequestBodySchema>;

export type EndWorkshopInterestApiArg = {
  id: string;
  endWorkshopInterestRequestBody: EndWorkshopInterestRequestBody;
};

export const WorkshopActivityManualTypeSchema = z.enum([
  'CALL_FROM_CUSTOMER',
  'CALL_TO_CUSTOMER',
  'MESSAGE_FROM_CUSTOMER',
  'MESSAGE_TO_CUSTOMER',
  'CUSTOMER_VISIT',
]);
export type WorkshopActivityManualType = z.infer<typeof WorkshopActivityManualTypeSchema>;

export const CreateWorkshopInterestActivityRequestBodySchema = z.object({
  activityType: WorkshopActivityManualTypeSchema,
  createdAt: z.string(),
  description: z.string().nullable(),
  subject: z.string().nullable().optional(),
});
export type CreateWorkshopInterestActivityRequestBody = z.infer<
  typeof CreateWorkshopInterestActivityRequestBodySchema
>;

export type CreateWorkshopInterestActivityApiArg = {
  interestId: string;
  createWorkshopInterestActivityRequestBody: CreateWorkshopInterestActivityRequestBody;
};

export type CreateWorkshopInterestActivityApiResponse = {
  id: string;
};

export const SelectVehicleRequestBodySchema = z.object({
  vehicleId: z.string().uuid(),
});
export type SelectVehicleRequestBody = z.infer<typeof SelectVehicleRequestBodySchema>;

export type SelectVehicleApiArg = {
  id: string;
  selectVehicleRequestBody: SelectVehicleRequestBody;
};

export type RemoveVehicleApiArg = {
  id: string;
};

export type GetWorkshopInterestUpcomingTasksApiResponse = TaskResponseBody[];
export type GetWorkshopInterestUpcomingTasksApiArg = {
  interestId: string;
};

export const ChangeWorkshopInterestLeadQualityRequestBodySchema = z.object({
  leadQuality: LeadQualityEnumSchema,
});
export type ChangeWorkshopInterestLeadQualityRequestBody = z.infer<
  typeof ChangeWorkshopInterestLeadQualityRequestBodySchema
>;

export type ChangeWorkshopInterestLeadQualityApiArg = {
  id: string;
  changeLeadQualityRequestBody: ChangeWorkshopInterestLeadQualityRequestBody;
};

export const SelectWorkshopInterestContactPersonRequestBodySchema = z.object({
  contactPersonId: z.string().nullable(),
});
export type SelectWorkshopInterestContactPersonRequestBody = z.infer<
  typeof SelectWorkshopInterestContactPersonRequestBodySchema
>;

export type SelectWorkshopInterestContactPersonApiArg = {
  id: string;
  selectWorkshopInterestContactPersonRequestBody: SelectWorkshopInterestContactPersonRequestBody;
};
